import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Switch } from "antd";
import CustomModal from "@/components/UI/Modals/CustomModal";
import {
  useGetAllPermissionsQuery,
  useGetPermissionQuery,
} from "@/services/api/permissions/permissionApi";
import { extractApiData } from "../../utils/dataUtils";

const { TextArea } = Input;

// Navigation structure - single source of truth
// standalone: true = no header, just individual permission
// subItems array = module with header and sub-permissions
const NAV_STRUCTURE = {
  MAIN: {
    order: 1,
    modules: {
      Dashboard: {
        order: 1,
        keywords: ["dashboard"],
        standalone: true,
      },
      "Orders & Bookings": {
        order: 2,
        keywords: ["order", "booking"],
        subItems: ["bulk orders", "catering orders", "archived orders"],
      },
      Transactions: {
        order: 3,
        keywords: ["transaction"],
        standalone: true,
      },
      Customers: {
        order: 4,
        keywords: ["customer"],
        standalone: true,
      },
    },
  },
  MANAGEMENT: {
    order: 2,
    modules: {
      "Company Management": {
        order: 1,
        keywords: ["company"],
        subItems: [
          "company setup",
          "staff",
          "revenue",
          "order and operations",
          "operations",
        ],
      },
      "Product Management": {
        order: 2,
        keywords: ["product"],
        subItems: [
          "category",
          "bulk purchase",
          "catering",
          "inventory control",
          "inventory",
        ],
      },
      "Customer Experience": {
        order: 3,
        keywords: ["experience"],
        subItems: [
          "promos & discounts",
          "promos and discounts",
          "feedback & ratings",
          "feedback and ratings",
          "terms & conditions",
          "terms and conditions",
        ],
      },
      Payroll: {
        order: 4,
        keywords: ["payroll"],
        subItems: ["reports and insights", "approval", "setup", "run"],
      },
    },
  },
};

// Normalize string for flexible matching
const normalize = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/&/g, "and");
};

// Categorize permission by name with prioritization for more specific matches
const categorizePermission = (permissionName) => {
  const normalized = normalize(permissionName);

  // Collect all possible matches with their specificity
  const matches = [];

  for (const [category, categoryConfig] of Object.entries(NAV_STRUCTURE)) {
    for (const [moduleName, moduleConfig] of Object.entries(
      categoryConfig.modules
    )) {
      const { keywords = [], subItems = [], standalone = false } = moduleConfig;

      if (standalone) {
        keywords.forEach((kw) => {
          const normalizedKw = normalize(kw);
          if (
            normalized.includes(normalizedKw) ||
            normalizedKw.includes(normalized)
          ) {
            matches.push({
              category,
              module: moduleName,
              isStandalone: true,
              categoryOrder: categoryConfig.order,
              moduleOrder: moduleConfig.order,
              matchLength: normalizedKw.length,
              isExactMatch: normalized === normalizedKw,
            });
          }
        });
      } else {
        subItems.forEach((sub) => {
          const ns = normalize(sub);
          if (
            normalized === ns ||
            normalized.includes(ns) ||
            ns.includes(normalized)
          ) {
            matches.push({
              category,
              module: moduleName,
              isStandalone: false,
              categoryOrder: categoryConfig.order,
              moduleOrder: moduleConfig.order,
              matchLength: ns.length,
              isExactMatch: normalized === ns,
            });
          }
        });
      }
    }
  }

  // No matches found
  if (matches.length === 0) {
    return {
      category: "MAIN",
      module: "Other",
      isStandalone: true,
      categoryOrder: 999,
      moduleOrder: 999,
    };
  }

  // Sort matches by priority:
  // 1. Exact matches first
  // 2. Then by longest match (most specific)
  // 3. Then by category order
  // 4. Then by module order
  matches.sort((a, b) => {
    if (a.isExactMatch !== b.isExactMatch)
      return b.isExactMatch - a.isExactMatch;
    if (a.matchLength !== b.matchLength) return b.matchLength - a.matchLength;
    if (a.categoryOrder !== b.categoryOrder)
      return a.categoryOrder - b.categoryOrder;
    return a.moduleOrder - b.moduleOrder;
  });

  // Return the best match
  const bestMatch = matches[0];
  return {
    category: bestMatch.category,
    module: bestMatch.module,
    isStandalone: bestMatch.isStandalone,
    categoryOrder: bestMatch.categoryOrder,
    moduleOrder: bestMatch.moduleOrder,
  };
};

// Transform and group permissions
const transformPermissions = (rawPermissions) => {
  if (!rawPermissions || !Array.isArray(rawPermissions)) {
    return { grouped: {}, flat: [] };
  }

  const transformed = rawPermissions.map((p) => {
    const { category, module, isStandalone, categoryOrder, moduleOrder } =
      categorizePermission(p.name);
    return {
      key: p.id.toString(),
      id: p.id,
      title: p.name || "Permission",
      description: p.description || "No description available",
      category,
      module,
      isStandalone,
      categoryOrder,
      moduleOrder,
    };
  });

  const grouped = transformed.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = {
        order: perm.categoryOrder,
        modules: {},
        standalonePermissions: [],
      };
    }

    if (perm.isStandalone) {
      acc[perm.category].standalonePermissions.push(perm);
    } else {
      if (!acc[perm.category].modules[perm.module]) {
        acc[perm.category].modules[perm.module] = {
          order: perm.moduleOrder,
          permissions: [],
        };
      }
      acc[perm.category].modules[perm.module].permissions.push(perm);
    }
    return acc;
  }, {});

  Object.values(grouped).forEach((cat) => {
    cat.standalonePermissions.sort((a, b) => a.moduleOrder - b.moduleOrder);
    Object.values(cat.modules).forEach((mod) =>
      mod.permissions.sort((a, b) => a.title.localeCompare(b.title))
    );
  });

  return { grouped, flat: transformed };
};

// Permission Icon Component
const PermissionIcon = () => (
  <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
    <svg
      className="w-4 h-4 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  </div>
);

// Single Permission Row Component - for standalone permissions (with box)
const PermissionRowBoxed = ({ permission, isEnabled, onChange, readOnly }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 mb-3 last:mb-0">
    <div className="flex items-center gap-3">
      <PermissionIcon />
      <div>
        <p className="text-sm font-medium text-gray-700">{permission.title}</p>
        <p className="text-xs text-gray-400">{permission.description}</p>
      </div>
    </div>
    <Switch
      checked={isEnabled}
      onChange={onChange}
      size="small"
      disabled={readOnly}
      style={{ backgroundColor: isEnabled ? "#10b981" : "#d1d5db" }}
    />
  </div>
);

// Sub-permission Row Component - for items under modules (no box, just divider)
const PermissionRowFlat = ({
  permission,
  isEnabled,
  onChange,
  isLast,
  readOnly,
}) => (
  <div
    className={`flex items-center justify-between py-4 ${
      !isLast ? "border-b border-gray-100" : ""
    }`}
  >
    <div className="flex items-center gap-3">
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <svg
          className="w-5 h-5 text-gray-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700">{permission.title}</p>
        <p className="text-xs text-gray-400">{permission.description}</p>
      </div>
    </div>
    <Switch
      checked={isEnabled}
      onChange={onChange}
      size="small"
      disabled={readOnly}
      style={{ backgroundColor: isEnabled ? "#10b981" : "#d1d5db" }}
    />
  </div>
);

const AddRoleModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  mode = "create", // 'create', 'edit', or 'view'
  roleData = null, // Role data for edit/view mode
}) => {
  const [form] = Form.useForm();
  const [permissions, setPermissions] = useState({});
  const [enableAll, setEnableAll] = useState(false);

  const { data: permissionsResponse, isLoading: isLoadingPermissions } =
    useGetAllPermissionsQuery();

  const { grouped: groupedPermissions, flat: flatPermissions } = useMemo(() => {
    const rawData = extractApiData(permissionsResponse);
    return transformPermissions(rawData);
  }, [permissionsResponse]);

  const sortedCategories = useMemo(() => {
    return Object.entries(groupedPermissions)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([name, data]) => ({
        name,
        standalonePermissions: data.standalonePermissions || [],
        modules: Object.entries(data.modules)
          .sort(([, a], [, b]) => a.order - b.order)
          .map(([moduleName, moduleData]) => ({
            name: moduleName,
            permissions: moduleData.permissions,
          })),
      }));
  }, [groupedPermissions]);

  // Initialize permissions from flat list
  useEffect(() => {
    if (flatPermissions.length > 0) {
      setPermissions(
        flatPermissions.reduce((acc, p) => ({ ...acc, [p.key]: false }), {})
      );
    }
  }, [flatPermissions]);

  // Initialize form and permissions when in edit/view mode or reset in create mode
  useEffect(() => {
    // console.log('Role modal initialization:', { mode, roleData });

    if (mode === "create") {
      // Reset form and permissions for create mode
      form.resetFields();
      setPermissions(
        flatPermissions.reduce((acc, p) => ({ ...acc, [p.key]: false }), {})
      );
      setEnableAll(false);
    } else if (
      (mode === "edit" || mode === "view") &&
      roleData &&
      flatPermissions.length > 0
    ) {
      // console.log("Setting form values with role data:", roleData);

      // Set form fields
      form.setFieldsValue({
        roleName: roleData.name,
        description: roleData.description,
      });

      // Set permissions - handle both array of IDs and array of permission objects
      const rolePermissionIds = Array.isArray(roleData.permissions)
        ? roleData.permissions.map((p) => {
            // If it's an object with pivot.permission_id
            if (p.pivot && p.pivot.permission_id) {
              return p.pivot.permission_id.toString();
            }
            // If it's an object with id
            if (p.id) {
              return p.id.toString();
            }
            // If it's just an ID
            return p.toString();
          })
        : [];

      // console.log("Role permission IDs:", rolePermissionIds);

      // Update permissions state
      const updatedPermissions = { ...permissions };
      rolePermissionIds.forEach((permId) => {
        if (updatedPermissions.hasOwnProperty(permId)) {
          updatedPermissions[permId] = true;
        }
      });

      setPermissions(updatedPermissions);

      // Check if all permissions are enabled
      const allEnabled =
        flatPermissions.length > 0 &&
        flatPermissions.every((p) => updatedPermissions[p.key]);
      setEnableAll(allEnabled);
    }
  }, [mode, roleData, flatPermissions, form]);

  const handleEnableAll = (checked) => {
    setEnableAll(checked);
    setPermissions(
      flatPermissions.reduce((acc, p) => ({ ...acc, [p.key]: checked }), {})
    );
  };

  const handlePermissionChange = (key, checked) => {
    const updated = { ...permissions, [key]: checked };
    setPermissions(updated);
    setEnableAll(
      flatPermissions.length > 0 && flatPermissions.every((p) => updated[p.key])
    );
  };

  const handleModuleToggle = (modulePermissions, checked) => {
    const updated = { ...permissions };
    modulePermissions.forEach((p) => {
      updated[p.key] = checked;
    });
    setPermissions(updated);
    setEnableAll(
      flatPermissions.length > 0 && flatPermissions.every((p) => updated[p.key])
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const permissionIds = Object.entries(permissions)
        .filter(([, enabled]) => enabled)
        .map(([key]) => parseInt(key, 10));

      const submitData = {
        role_name: values.roleName,
        description: values.description,
        permissions: permissionIds,
        status: true,
      };

      // Include role ID when updating
      if (mode === "edit" && roleData?.id) {
        submitData.id = roleData.id;
      }

      await onSubmit(submitData);

      // Reset form only in create mode
      if (mode === "create") {
        form.resetFields();
        setPermissions({});
        setEnableAll(false);
      }

      onClose();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const isProcessing = loading || isLoadingPermissions;
  const isReadOnly = mode === "view";

  // Modal configuration based on mode
  const modalConfig = {
    create: {
      title: "Roles and Permissions Setup",
      subtitle:
        "Kindly fill in the details below to setup User role and permissions.",
      primaryActionText: "Create →",
    },
    edit: {
      title: "Edit Role",
      subtitle: "Update the role details and permissions below.",
      primaryActionText: "Update →",
    },
    view: {
      title: "View Role",
      subtitle: "View the role details and permissions below.",
      primaryActionText: null,
    },
  };

  const config = modalConfig[mode] || modalConfig.create;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      subtitle={config.subtitle}
      width={900}
      primaryActionText={config.primaryActionText}
      onPrimaryAction={handleSubmit}
      secondaryActionText="Cancel"
      onSecondaryAction={onClose}
      hideSecondaryAction={isReadOnly}
      primaryActionDisabled={isProcessing}
      loading={isProcessing}
      noActionButton={isReadOnly}
      primaryButtonColor="orange"
    >
      <Form form={form}>
        <div className="p-6 space-y-6">
          {isLoadingPermissions && (
            <div className="text-center py-4">
              <p className="text-gray-500">Loading permissions...</p>
            </div>
          )}

          {!isLoadingPermissions && flatPermissions.length === 0 && (
            <div className="text-center py-4">
              <p className="text-gray-500">No permissions available</p>
            </div>
          )}

          {/* Role Details */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Role name <span className="text-red-500">*</span>
              </label>
              <Form.Item
                name="roleName"
                rules={[{ required: true, message: "Please enter role name" }]}
                className="mb-0"
              >
                <Input
                  placeholder="e.g Sales Manager"
                  size="large"
                  style={{ backgroundColor: "#f9fafb" }}
                  readOnly={isReadOnly}
                  // disabled={isReadOnly}
                />
              </Form.Item>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                Description <span className="text-red-500">*</span>
              </label>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
                className="mb-0"
              >
                <TextArea
                  placeholder="e.g manage sales team and sales operations"
                  rows={3}
                  style={{ backgroundColor: "#f9fafb" }}
                  readOnly={isReadOnly}
                  // disabled={isReadOnly}
                />
              </Form.Item>
            </div>
          </div>

          {/* Permissions Section */}
          {flatPermissions.length > 0 && (
            <div className="border border-gray-100 rounded-lg mt-5">
              {/* Header */}
              <div className="flex justify-between items-center px-4 py-4">
                <h3 className="font-semibold text-gray-900 text-base">
                  Set permissions
                </h3>
                {!isReadOnly && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Enable all</span>
                    <Switch
                      checked={enableAll}
                      onChange={handleEnableAll}
                      size="small"
                      style={{
                        backgroundColor: enableAll ? "#10b981" : "#d1d5db",
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="border-b border-gray-200" />

              {/* Permissions List */}
              <div className="py-4">
                {sortedCategories.map((category, catIdx) => (
                  <div key={category.name}>
                    {/* Category Header - Orange */}
                    <div className="px-4 py-3">
                      <span className="text-xs font-semibold text-orange-500 uppercase tracking-wider">
                        {category.name}
                      </span>
                    </div>

                    {/* Standalone Permissions */}
                    {category.standalonePermissions.length > 0 && (
                      <div className="px-4 space-y-3">
                        {category.standalonePermissions.map((permission) => (
                          <PermissionRowBoxed
                            key={permission.key}
                            permission={permission}
                            isEnabled={permissions[permission.key] || false}
                            onChange={(checked) =>
                              handlePermissionChange(permission.key, checked)
                            }
                            readOnly={isReadOnly}
                          />
                        ))}
                      </div>
                    )}

                    {/* Modules with Sub-permissions */}
                    {category.modules.map((module) => (
                      <div
                        key={module.name}
                        className="mt-4 mx-4 border border-gray-100 rounded-lg"
                      >
                        {/* Module Header */}
                        <div className="flex justify-between items-center px-4 py-4 border-b border-gray-100">
                          <span className="text-sm font-medium text-gray-800">
                            {module.name}
                          </span>
                          <Switch
                            checked={module.permissions.every(
                              (p) => permissions[p.key]
                            )}
                            onChange={(checked) =>
                              handleModuleToggle(module.permissions, checked)
                            }
                            size="small"
                            disabled={isReadOnly}
                            style={{
                              backgroundColor: module.permissions.every(
                                (p) => permissions[p.key]
                              )
                                ? "#10b981"
                                : "#d1d5db",
                            }}
                          />
                        </div>

                        {/* Sub-permissions */}
                        <div className="px-4">
                          {module.permissions.map((permission, idx) => (
                            <PermissionRowFlat
                              key={permission.key}
                              permission={permission}
                              isEnabled={permissions[permission.key] || false}
                              onChange={(checked) =>
                                handlePermissionChange(permission.key, checked)
                              }
                              isLast={idx === module.permissions.length - 1}
                              readOnly={isReadOnly}
                            />
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Divider between categories */}
                    {catIdx < sortedCategories.length - 1 && (
                      <div className="border-b border-gray-200 my-4 mx-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Form>
    </CustomModal>
  );
};

export default AddRoleModal;
